import React, { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import secureLocalStorage from "react-secure-storage";
import { ToastContainer, toast } from 'react-toastify'
import { AiTwotoneDelete, AiOutlineHeart } from 'react-icons/ai'
import { BsArrowLeft } from 'react-icons/bs'
import { useCart } from "./CartContext";


// Components
import Searchbar from './Shearchbar'
import Navbar from './Navbar'
import Downloadapp from './Downloadapp'
import Security from './Security'
import Footer from './Footer'
import Manubar from './Manubar'

// Styles
import './header.css'
import 'react-toastify/dist/ReactToastify.css'

const FavouriteWishlist = () => {
  const navigate = useNavigate();
  const { triggerCartRefresh } = useCart();
  
  // State
  const [favouriteList, setFavouriteList] = useState([])
  const [loading, setLoading] = useState(true)
  const [removingItem, setRemovingItem] = useState(null)

  // Get data from storage
  const _id = secureLocalStorage.getItem("n_id")
  const token = secureLocalStorage.getItem("n_token")

  // Fetch favourite products
  const fetchFavouriteProducts = useCallback(async () => {
    if (!_id || !token) {
      toast.error("Please login to view your favourites")
      navigate("/login")
      return
    }

    try {
      setLoading(true)
      const payload = {
        user_id: _id
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API}website/get_favourite_product_list`,
        payload,
        {
          headers: {
            token: token,
          }
        }
      )

      if (response.data.result) {
        setFavouriteList(response.data.data || [])
      } else {
        toast.error("Failed to load favourite products")
        setFavouriteList([])
      }
    } catch (error) {
      console.error("Error fetching favourites:", error)
      setFavouriteList([])
    } finally {
      setLoading(false)
    }
  }, [_id, token, navigate])

  useEffect(() => {
    fetchFavouriteProducts()
  }, [fetchFavouriteProducts])

  // Handle product click
  const handleProductClick = (productId) => {
    secureLocalStorage.setItem("n_singleproducts", productId)
    navigate(`/single_products/${productId}`)
  }

  // Remove from favourites
  const removeFromFavourites = async (productId, productName) => {
    if (!_id || !token) {
      toast.error("Please login to manage favourites")
      return
    }

    try {
      setRemovingItem(productId)
      const payload = {
        user_id: _id,
        product_id: productId
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API}website/add_favourite_product`,
        payload,
        {
          headers: {
            token: token,
          }
        }
      )

      if (response.data.result) {
        toast.success(`❤️ ${productName} removed from favourites`)
        triggerCartRefresh();
        setFavouriteList(prev => prev.filter(item => item.product_id !== productId))
      }
    } catch (error) {
      console.error("Error removing favourite:", error)
      toast.success("Product remove from favourites");
      triggerCartRefresh();
      fetchFavouriteProducts();
    } finally {
      setRemovingItem(null)
    }
  }

  // Handle empty state
  const renderEmptyState = () => (
    <div className="text-center py-5">
      <div className="empty-favourites-icon mb-4">
        <AiOutlineHeart size={80} color="#ddd" />
      </div>
      <h3 className="text-muted mb-3">Your Favourites List is Empty</h3>
      <p className="text-muted mb-4">Start adding items you love to your favourites!</p>
      <Link 
        to="/" 
        className="btn btn-primary btn-lg"
      >
        Explore Products
      </Link>
    </div>
  )

  // Handle loading state
  const renderLoadingState = () => (
    <div className="text-center py-5">
      <div className="spinner-border text-primary mb-3" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="text-muted">Loading your favourite items...</p>
    </div>
  )

  return (
    <div className="favourites-page">
      <ToastContainer 
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Header Section */}
      <div className="modal fade offcanvas-modal" id="exampleModal">
        <div className="modal-dialog offcanvas-dialog">
          <div className="modal-content">
            <Manubar />
          </div>
        </div>
      </div>

      <header>
        <Searchbar />
        <Navbar />
      </header>

      {/* Breadcrumb */}
      <nav className="breadcrumb-section bg-light py-3">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <ol className="breadcrumb m-0 p-0 align-items-center">
                <li className="breadcrumb-item">
                  <Link to="/" className="text-decoration-none text-muted">
                    <BsArrowLeft className="me-2" />
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item active text-dark fw-semibold">
                  My Favourites
                </li>
              </ol>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <section className="favourites-section py-5">
        <div className="container">
          <div className="row">
            <div className="col-12">
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h1 className="h2 fw-bold text-dark mb-2">
                    My Favourites <span className="text-danger">❤</span>
                  </h1>
                  <p className="text-muted mb-0">
                    {favouriteList.length} item{favouriteList.length !== 1 ? 's' : ''} in your wishlist
                  </p>
                </div>
                
              </div>

              {/* Favourites List */}
              {loading ? (
                renderLoadingState()
              ) : favouriteList.length === 0 ? (
                renderEmptyState()
              ) : (
                <div className="row g-4">
                  {favouriteList.map((item) => (
                    <div key={item.product_id} className="col-lg-6 col-md-6 col-sm-12">
                      <div className="favourite-item-card card h-100 border-0 shadow-sm">
                        <div className="card-body">
                          <div className="row align-items-center h-100">
                            {/* Product Image */}
                            <div className="col-3 text-center">
                              <img 
                                src={`${process.env.REACT_APP_API}uploads/${item.product_image}`}
                                className="img-fluid rounded cursor-pointer"
                                alt={item.product_name}
                                onClick={() => handleProductClick(item.product_id)}
                                style={{
                                  width: '100%',
                                  height: '100px',
                                  objectFit: 'cover',
                                  cursor: 'pointer'
                                }}
                              />
                            </div>

                            {/* Product Details */}
                            <div className="col-7">
                              <h6 
                                className="product-name fw-semibold mb-2 cursor-pointer"
                                onClick={() => handleProductClick(item.product_id)}
                                style={{ cursor: 'pointer' }}
                              >
                                {item.product_name}
                              </h6>
                              
                              <div className="pricing-info">
                                <span className="text-primary fw-bold fs-5">
                                  ₹{item.price?.[0]?.toFixed(2) || '0.00'}
                                </span>
                                {item.mrp > item.price?.[0] && (
                                  <span className="text-muted text-decoration-line-through ms-2">
                                    ₹{item.mrp?.toFixed(2)}
                                  </span>
                                )}
                              </div>

                              {item.mrp > item.price?.[0] && (
                                <div className="savings-badge mt-1">
                                  <span className="badge bg-success">
                                    Save ₹{(item.mrp - item.price?.[0])?.toFixed(2)}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Remove Button */}
                            <div className="col-2 text-center">
                              <button
                                className="btn btn-outline-danger btn-sm rounded-circle p-2"
                                onClick={() => removeFromFavourites(item.product_id, item.product_name)}
                                disabled={removingItem === item.product_id}
                                title="Remove from favourites"
                              >
                                {removingItem === item.product_id ? (
                                  <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Removing...</span>
                                  </div>
                                ) : (
                                  <AiTwotoneDelete size={18} />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              
            </div>
          </div>
        </div>
      </section>

      {/* Additional Sections */}
      <Downloadapp />
      <Security />
      <Footer />
    </div>
  )
}

export default FavouriteWishlist